import { useCallback, useEffect, useRef, useState } from "react";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";
import { qpRail } from "../../lib/qpRail";
import { QpFrame } from "./QpFrame";
import {
  embedApi,
  statusClass,
  statusLabel,
  type EmbedPdf,
  type EmbedSession as Session,
} from "./embedApi";

const POLL_MS = 5000;

// Session view: slim header bar (client, flow, live QP status, escape hatch),
// then the QP frame — or, when QP refuses to be framed from this origin, a
// clear card with "Open in new tab" instead of a broken grey frame. Polls the
// session every 5 s while the tab is visible and the session is open.
export function EmbedSession({
  sessionId,
  initial,
  onExit,
}: {
  sessionId: number;
  initial: Session | null;
  onExit: () => void;
}) {
  const [session, setSession] = useState<Session | null>(initial);
  const [pdfs, setPdfs] = useState<EmbedPdf[]>([]);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const inflight = useRef(false);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const d = await embedApi.session(sessionId);
      setSession(d.session);
      // Keep the last known PDFs if a poll fails to read them.
      if (d.pdfs.length > 0 || !d.refreshError) setPdfs(d.pdfs);
      setRefreshError(d.refreshError);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      inflight.current = false;
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isOpen = session?.state === "open";

  // Poll only while the session is open and the tab is visible; catch up
  // immediately when the tab becomes visible again.
  useEffect(() => {
    if (!isOpen) return;
    const tick = () => {
      if (document.visibilityState === "visible") void load();
    };
    const timer = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [isOpen, load]);

  async function close() {
    if (!session || session.state !== "open") return onExit();
    setClosing(true);
    try {
      await embedApi.close(session.id);
      onExit();
    } catch (e) {
      setErr((e as Error).message);
      setClosing(false);
    }
  }

  if (!session) {
    return (
      <div className="page">
        {err ? <div className="banner-error">{err}</div> : <p className="muted">Loading session…</p>}
        <div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onExit}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const done = session.qp_status === "completed" || session.qp_status === "signing";

  return (
    <div className="page" style={{ gap: "var(--s-md)" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--s-md)",
          flexWrap: "wrap",
          padding: "var(--s-sm) var(--s-md)",
          background: "var(--c-paper)",
          border: "1px solid var(--c-border)",
          borderRadius: "var(--r-md)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--s-sm)", minWidth: 0 }}>
          {session.investor_type && (
            <ClientTypeIcon type={session.investor_type} className="title-type-icon" />
          )}
          <strong style={{ color: "var(--c-fjord)" }}>{session.investor_name ?? "Investor"}</strong>
          <span className="muted">· {session.flow_name ?? "Flow session"}</span>
          {session.qp_status && (
            <span className={`pill ${statusClass(session.qp_status)}`} style={{ marginLeft: "var(--s-sm)" }}>
              {statusLabel(session.qp_status)}
            </span>
          )}
          {session.qp_external_status && (
            <span className={`pill ext-${session.qp_external_status}`}>
              {session.qp_external_status}
            </span>
          )}
          {session.state !== "open" && (
            <span className="pill fstatus-inactive">session {session.state}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: "var(--s-sm)" }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => qpRail.toggle()}>
            QP activity
          </button>
          {session.embed_url && (
            <a
              className="btn btn-ghost btn-sm"
              href={session.embed_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab ↗
            </a>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={close}
            disabled={closing}
          >
            {session.state === "open" ? (closing ? "Closing…" : "Close") : "Back"}
          </button>
        </div>
      </header>

      {err && <div className="banner-error">{err}</div>}
      {refreshError && (
        <p className="muted" style={{ margin: 0 }}>
          Couldn't refresh the Quantfolio status just now — retrying. ({refreshError})
        </p>
      )}

      {done && <CompletionBanner status={session.qp_status!} pdfs={pdfs} />}

      <SessionBody session={session} />
    </div>
  );
}

function SessionBody({ session }: { session: Session }) {
  if (session.state === "failed" || !session.embed_url) {
    return (
      <div className="banner-error">
        This session could not be opened{session.error ? `: ${session.error}` : "."}
      </div>
    );
  }
  if (session.embeddable === 0) {
    return <NotEmbeddableCard url={session.embed_url} block={session.embed_block} />;
  }
  return (
    <>
      {session.embeddable === null && (
        <p className="muted" style={{ margin: 0 }}>
          Couldn't verify that Quantfolio allows embedding
          {session.embed_block ? ` (${session.embed_block})` : ""}. If the frame stays
          blank, use “Open in new tab”.
        </p>
      )}
      <QpFrame embedUrl={session.embed_url} flowName={session.flow_name} />
    </>
  );
}

function NotEmbeddableCard({ url, block }: { url: string; block: string | null }) {
  return (
    <div className="empty" style={{ padding: "var(--s-xxl) var(--s-xl)" }}>
      <span className="eyebrow">Not embeddable yet</span>
      <h2 style={{ color: "var(--c-fjord)", margin: 0 }}>
        Quantfolio does not allow embedding from this origin yet
      </h2>
      <p className="muted" style={{ margin: 0, maxWidth: "36rem" }}>
        Quantfolio answered with <code>{block ?? "a framing restriction"}</code>, so the
        browser would refuse to show it here. The session is ready — continue it in a
        new tab, signed in through Andersen as usual.
      </p>
      <a className="btn btn-primary" href={url} target="_blank" rel="noopener noreferrer">
        Open in new tab ↗
      </a>
    </div>
  );
}

function CompletionBanner({ status, pdfs }: { status: string; pdfs: EmbedPdf[] }) {
  return (
    <div className="banner-success">
      <strong>
        {status === "completed"
          ? "Flow completed in Quantfolio."
          : "Ready for signing — signing may open in a popup window."}
      </strong>
      {pdfs.length > 0 ? (
        <ul style={{ margin: "var(--s-xs) 0 0", paddingLeft: "var(--s-lg)" }}>
          {pdfs.map((p) => (
            <li key={p.id}>
              {p.url ? (
                <a href={p.url} target="_blank" rel="noopener noreferrer">
                  {p.name}
                </a>
              ) : (
                p.name
              )}
            </li>
          ))}
        </ul>
      ) : (
        <span> No documents have been generated yet.</span>
      )}
    </div>
  );
}
